import React, { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Grid,
  Paper,
  Typography,
  Button,
  Menu,
  MenuItem,
} from "@mui/material";
import Pagination from "../../../common/Pagination";
import SearchBar from "../../../common/SearchBar";
import axios from "axios";
import PerPageSelect from "../../../common/PerPageSelect";
import * as XLSX from "xlsx";
import jsPDF from "jspdf";
import "jspdf-autotable";

const API_URL = process.env.REACT_APP_API_URL;

const OrganizationNameSearchPageUtility = () => {
  const userRole = sessionStorage.getItem("user_type");
  const isViewer = userRole === "V";
  const isEditor = userRole === "E";
  const [contactData, setContactData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [perPage, setPerPage] = useState(15);
  const [currentPage, setCurrentPage] = useState(1);
  const location = useLocation();
  const navigate = useNavigate();

  // Get selected organization names from location state
  const { orgNames, selectedOrgNames } = location.state || {};

  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  // Fetch contact data by organization names
  const fetchContactData = async () => {
    setLoading(true);
    setError("");

    try {
      const response = await axios.get(`${API_URL}/contact_data_by_orgname`, {
        params: {
          org_names: selectedOrgNames.join(","),
        },
      });

      setContactData(response.data);
      setFilteredData(response.data);
      setLoading(false);
    } catch (err) {
      setError("Error fetching data");
      setLoading(false);
    }
  };

  useEffect(() => {
    if (orgNames && orgNames.length > 0) {
      fetchContactData();
    }
  }, [orgNames]);

  // Filter the data based on the search term
  useEffect(() => {
    const filtered = contactData.filter((post) => {
      const searchTermLower = searchTerm.toLowerCase();
      return Object.keys(post).some((key) => {
        const value = post[key];
        return (
          value !== null &&
          value !== undefined &&
          String(value).toLowerCase().includes(searchTermLower)
        );
      });
    });

    setFilteredData(filtered);
    setCurrentPage(1);
  }, [searchTerm, contactData]);

  const handlePerPageChange = (event) => {
    setPerPage(event.target.value);
    setCurrentPage(1);
  };

  const handleSearchTermChange = (event) => {
    setSearchTerm(event.target.value);
  };

  const pageCount = Math.ceil(filteredData.length / perPage);

  const paginatedPosts = filteredData.slice(
    (currentPage - 1) * perPage,
    currentPage * perPage
  );

  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  const getSafeFileName = (rawName, maxLength = 100) => {
  const sanitized = rawName.replace(/[^\w\s]/g, "").trim();
  return sanitized.length > maxLength
    ? sanitized.substring(0, maxLength) + "..."
    : sanitized || "Contact_Report";
};


  const exportToExcel = () => {
  const headers = [
    "Organization Name",
    "Owner Name",
    "Designation",
    "Address",
    "City Name",
    "State",
    "Country",
    "Mobile No",
    "Email",
    "Website URL",
    "Anniversary",
    "DOB",
  ];

  const rows = filteredData.map((data) => [
    data.org_name || "",
    data.org_holder_name || "",
    data.designation || "",
    data.office_address || "",
    data.city || "",
    data.state || "",
    data.country || "",
    formatMultipleValues(data.mobile_no) || "",
    formatMultipleValues(data.email) || "",
    data.website || "",
    data.anniversary || "",
    data.DOB || "",
  ]);

  const worksheetData = [headers, ...rows];
  const worksheet = XLSX.utils.aoa_to_sheet(worksheetData);
  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, "Contact Data");

  const fileName = getSafeFileName(
    selectedOrgNames?.join("_") || "Contact_Data_Search",
    100
  );

  XLSX.writeFile(workbook, `${fileName}.xlsx`);
};


const exportToPDF = () => {
  const doc = new jsPDF("landscape", "mm", "a4");

  const titleText =
    selectedOrgNames && selectedOrgNames.length > 0
      ? selectedOrgNames.join(", ")
      : "Client Connect Report";

  const fileName = getSafeFileName(titleText, 100);

  const tableColumn = [
    "Organization Name",
    "Owner Name",
    "Designation",
    "Address",
    "City",
    "State",
    "Country",
    "Mobile",
    "Email",
    "Website",
  ];

  const tableRows = filteredData.map((data) => [
    data.org_name || "",
    data.org_holder_name || "",
    data.designation || "",
    data.office_address || "",
    data.city || "",
    data.state || "",
    data.country || "",
    formatMultipleValues(data.mobile_no) || "",
    formatMultipleValues(data.email) || "",
    data.website || "",
  ]);

  // --- Title setup ---
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const marginX = 10;
  const usableWidth = pageWidth - marginX * 2;

  const wrappedTitle = doc.splitTextToSize(titleText, usableWidth);
  const titleY = 15;

  doc.setFontSize(14);
  wrappedTitle.forEach((line, index) => {
    const x = (pageWidth - doc.getTextWidth(line)) / 2;
    doc.text(line, x, titleY + index * 7);
  });

  const tableStartY = titleY + wrappedTitle.length * 7 + 5;

  // --- Perfect width allocation (sum = 1.00) ---
  const colWidths = {
    0: usableWidth * 0.14, // Org
    1: usableWidth * 0.10, // Owner
    2: usableWidth * 0.07, // Designation
    3: usableWidth * 0.23, // Address
    4: usableWidth * 0.06, // City
    5: usableWidth * 0.05, // State
    6: usableWidth * 0.05, // Country
    7: usableWidth * 0.08, // Mobile
    8: usableWidth * 0.13, // Email
    9: usableWidth * 0.09, // Website
  };

  doc.autoTable({
    head: [tableColumn],
    body: tableRows,
    startY: tableStartY,
    theme: "grid",
    tableWidth: usableWidth,
    margin: { left: marginX, right: marginX },

    styles: {
      fontSize: 8,
      cellPadding: 2,
      overflow: "linebreak",
    },

    headStyles: {
      fillColor: [230, 230, 230],
      textColor: 0,
      fontStyle: "bold",
      halign: "center",
    },

    columnStyles: {
      0: { cellWidth: colWidths[0] },
      1: { cellWidth: colWidths[1] },
      2: { cellWidth: colWidths[2] },
      3: { cellWidth: colWidths[3] },
      4: { cellWidth: colWidths[4] },
      5: { cellWidth: colWidths[5] },
      6: { cellWidth: colWidths[6] },
      7: { cellWidth: colWidths[7] },
      8: { cellWidth: colWidths[8] },
      9: { cellWidth: colWidths[9] },
    },

    didDrawPage: function (data) {
      doc.setFontSize(8);
      doc.text(`Page ${data.pageNumber}`, marginX, pageHeight - 5);
    },
  });

  doc.save(`${fileName}.pdf`);
};

  // Helper function to format multiple values in one cell (for mobile numbers or emails)
  const formatMultipleValues = (value) => {
    if (!value) return "";
    // Check if the value contains multiple entries (comma-separated or space-separated)
    const values = value.split(/[, ]+/);
    return values.join("\n"); // Join with a newline to place values one below another
  };

  const handleRowClick = (contact_Id) => {
    const selectedRecord = filteredData.find(
      (record) => record.contact_Id === contact_Id
    );
    navigate("/contactData", { state: { selectedRecord } });
  };

  return (
    <div style={{
      padding: "10px",
      maxWidth: "100vw", // Ensure full-width on all screens
      overflowX: "hidden", // Prevent horizontal scrolling
    }}>
      <Typography  variant="h4"
    gutterBottom
    textAlign="center"
    sx={{
      fontSize: {
        xs: "20px", // Smaller font size for extra small screens
        sm: "24px",
        md: "28px",
        lg: "32px", // Larger font size for large screens
      },
    }}>
        {selectedOrgNames && selectedOrgNames.length > 0
          ? `Report for: ${selectedOrgNames.join(", ")}`
          : "Contact Data Search"}
      </Typography>

      {loading && (
        <Typography variant="h6" gutterBottom textAlign="center">
          Loading...
        </Typography>
      )}
      {error && (
        <Typography
          variant="h6"
          gutterBottom
          textAlign="center"
          style={{ color: "red" }}
        >
          {error}
        </Typography>
      )}

      <Grid container
    justifyContent="flex-end"
    spacing={2}
    sx={{
      marginBottom: 2,
      width: "100%", // Ensure full width
      maxWidth: "100%", // Prevent layout overflow
    }}>
        {!isViewer && !isEditor && (
  <Button variant="contained" onClick={handleClick}>
    Export To
  </Button>
)}
        <Menu anchorEl={anchorEl} open={open} onClose={handleClose}>
          <MenuItem
            onClick={() => {
              exportToExcel();
              handleClose();
            }}
          >
            Excel
          </MenuItem>
          <MenuItem
            onClick={() => {
              exportToPDF();
              handleClose();
            }}
          >
            PDF
          </MenuItem>
        </Menu>
      </Grid>

      {/* Search Bar */}
      <Grid
        container
        spacing={2}
        alignItems="center"
        justifyContent="flex-end"
        sx={{ marginTop: 2 }}
      >
        <Grid item>
          <PerPageSelect value={perPage} onChange={handlePerPageChange} />
        </Grid>
        <Grid item xs={12} sm={4}>
          <SearchBar value={searchTerm} onChange={handleSearchTermChange} />
        </Grid>
      </Grid>

      {/* Contact Data Table */}
      {!loading && (
        <>
          <TableContainer
            component={Paper}
            sx={{
              marginTop: 2,
              maxHeight: "70vh",
              maxWidth: "90%",
              width: "80vw",
              marginLeft:25
            }}
          >
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Organization Name</TableCell>
                  <TableCell>Owner Name</TableCell>
                  <TableCell>Designation</TableCell>
                   <TableCell>Address</TableCell>

                  <TableCell>City Name</TableCell>
                  <TableCell>State</TableCell>
                  <TableCell>Country</TableCell>

                  <TableCell>Mobile No</TableCell>
                  <TableCell>Email</TableCell>

                  <TableCell>Website URL</TableCell>
                  <TableCell>Anniversaty</TableCell>
                  <TableCell>DOB</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {paginatedPosts.map((data) => (
                  <TableRow
                    key={data.contact_Id}
                    style={{ cursor: "pointer" }}
                    onDoubleClick={() => handleRowClick(data.contact_Id)}
                  >
                    <TableCell>{data.org_name || ""}</TableCell>
                    <TableCell>{data.org_holder_name || ""}</TableCell>
                    <TableCell>{data.designation || ""}</TableCell>
                      <TableCell>{data.office_address || ""}</TableCell>


                    <TableCell>{data.city || ""}</TableCell>
                    <TableCell>{data.state || ""}</TableCell>
                    <TableCell>{data.country || ""}</TableCell>

                    <TableCell>{data.mobile_no || ""}</TableCell>
                    <TableCell>{data.email || ""}</TableCell>

                    <TableCell>{data.website || ""}</TableCell>
                    <TableCell>{data.anniversary || ""}</TableCell>
                    <TableCell>{data.DOB || ""}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          <Grid container justifyContent="center" sx={{ marginTop: 3 }}>
            <Pagination
              pageCount={pageCount}
              currentPage={currentPage}
              onPageChange={handlePageChange}
            />
          </Grid>
        </>
      )}
    </div>
  );
};

export default OrganizationNameSearchPageUtility;
